/**
 * Centralized Food Icon Library
 *
 * This file contains all food icons used throughout the app with:
 * - Icon component references from Lucide
 * - Color associations (CSS variable names)
 * - Category classifications
 * - Easy extensibility for new icons
 */

import type { LucideIcon } from "lucide-react";
import {
  Apple,
  Banana,
  Bean,
  Beef,
  Cake,
  CakeSlice,
  Candy,
  CandyCane,
  Carrot,
  Cherry,
  Citrus,
  Coffee,
  Cookie,
  Croissant,
  Dessert,
  Donut,
  Drumstick,
  Egg,
  EggFried,
  Fish,
  Grape,
  Ham,
  Hop,
  IceCreamBowl,
  IceCreamCone,
  LeafyGreen,
  Lollipop,
  Milk,
  Nut,
  Pizza,
  Popcorn,
  Popsicle,
  Salad,
  Sandwich,
  Shell,
  Snail,
  Soup,
  Torus,
  Vegan,
} from "lucide-react";

/**
 * Food categories for grouping
 */
export type FoodCategory =
  | "meat"
  | "seafood"
  | "produce"
  | "fruit"
  | "dairy"
  | "dessert"
  | "prepared"
  | "beverage"
  | "other";

/**
 * Food icon definition
 */
export interface FoodIcon {
  /** Lucide icon component */
  icon: LucideIcon;
  /** Icon identifier (kebab-case, matches Lucide name) */
  id: string;
  /** Display name */
  name: string;
  /** Category classification */
  category: FoodCategory;
  /** CSS variable name for text color (without 'text-' prefix) */
  colorClass: string;
}

/**
 * Complete food icon registry
 * Alphabetically sorted within categories for maintainability
 */
export const FOOD_ICONS: FoodIcon[] = [
  // MEAT
  { icon: Beef, id: "beef", name: "Beef", category: "meat", colorClass: "food-beef-text" },
  {
    icon: Drumstick,
    id: "drumstick",
    name: "Drumstick",
    category: "meat",
    colorClass: "food-drumstick-text",
  },
  { icon: Ham, id: "ham", name: "Ham", category: "meat", colorClass: "food-ham-text" },

  // SEAFOOD
  { icon: Fish, id: "fish", name: "Fish", category: "seafood", colorClass: "food-fish-text" },
  { icon: Shell, id: "shell", name: "Shell", category: "seafood", colorClass: "food-shell-text" },
  { icon: Snail, id: "snail", name: "Snail", category: "seafood", colorClass: "food-snail-text" },

  // PRODUCE
  { icon: Bean, id: "bean", name: "Bean", category: "produce", colorClass: "food-bean-text" },
  {
    icon: Carrot,
    id: "carrot",
    name: "Carrot",
    category: "produce",
    colorClass: "food-carrot-text",
  },
  { icon: Hop, id: "hop", name: "Hop", category: "produce", colorClass: "food-hop-text" },
  {
    icon: LeafyGreen,
    id: "leafy-green",
    name: "Leafy Green",
    category: "produce",
    colorClass: "food-leafy-green-text",
  },
  { icon: Salad, id: "salad", name: "Salad", category: "produce", colorClass: "food-salad-text" },
  { icon: Vegan, id: "vegan", name: "Vegan", category: "produce", colorClass: "food-vegan-text" },

  // FRUIT
  { icon: Apple, id: "apple", name: "Apple", category: "fruit", colorClass: "food-apple-text" },
  { icon: Banana, id: "banana", name: "Banana", category: "fruit", colorClass: "food-banana-text" },
  { icon: Cherry, id: "cherry", name: "Cherry", category: "fruit", colorClass: "food-cherry-text" },
  { icon: Citrus, id: "citrus", name: "Citrus", category: "fruit", colorClass: "food-citrus-text" },
  { icon: Grape, id: "grape", name: "Grape", category: "fruit", colorClass: "food-grape-text" },

  // DAIRY
  {
    icon: Croissant,
    id: "croissant",
    name: "Croissant",
    category: "dairy",
    colorClass: "food-croissant-text",
  },
  { icon: Egg, id: "egg", name: "Egg", category: "dairy", colorClass: "food-egg-text" },
  {
    icon: EggFried,
    id: "egg-fried",
    name: "Fried Egg",
    category: "dairy",
    colorClass: "food-egg-fried-text",
  },
  { icon: Milk, id: "milk", name: "Milk", category: "dairy", colorClass: "food-milk-text" },

  // DESSERT
  { icon: Cake, id: "cake", name: "Cake", category: "dessert", colorClass: "food-cake-text" },
  {
    icon: CakeSlice,
    id: "cake-slice",
    name: "Cake Slice",
    category: "dessert",
    colorClass: "food-cake-slice-text",
  },
  { icon: Candy, id: "candy", name: "Candy", category: "dessert", colorClass: "food-candy-text" },
  {
    icon: CandyCane,
    id: "candy-cane",
    name: "Candy Cane",
    category: "dessert",
    colorClass: "food-candy-cane-text",
  },
  {
    icon: Cookie,
    id: "cookie",
    name: "Cookie",
    category: "dessert",
    colorClass: "food-cookie-text",
  },
  {
    icon: Dessert,
    id: "dessert",
    name: "Dessert",
    category: "dessert",
    colorClass: "food-dessert-text",
  },
  { icon: Donut, id: "donut", name: "Donut", category: "dessert", colorClass: "food-donut-text" },
  {
    icon: IceCreamBowl,
    id: "ice-cream-bowl",
    name: "Ice Cream Bowl",
    category: "dessert",
    colorClass: "food-ice-cream-bowl-text",
  },
  {
    icon: IceCreamCone,
    id: "ice-cream-cone",
    name: "Ice Cream Cone",
    category: "dessert",
    colorClass: "food-ice-cream-cone-text",
  },
  {
    icon: Lollipop,
    id: "lollipop",
    name: "Lollipop",
    category: "dessert",
    colorClass: "food-lollipop-text",
  },
  {
    icon: Popsicle,
    id: "popsicle",
    name: "Popsicle",
    category: "dessert",
    colorClass: "food-popsicle-text",
  },

  // PREPARED FOODS
  { icon: Pizza, id: "pizza", name: "Pizza", category: "prepared", colorClass: "food-pizza-text" },
  {
    icon: Popcorn,
    id: "popcorn",
    name: "Popcorn",
    category: "prepared",
    colorClass: "food-popcorn-text",
  },
  {
    icon: Sandwich,
    id: "sandwich",
    name: "Sandwich",
    category: "prepared",
    colorClass: "food-sandwich-text",
  },
  { icon: Soup, id: "soup", name: "Soup", category: "prepared", colorClass: "food-soup-text" },

  // BEVERAGE
  {
    icon: Coffee,
    id: "coffee",
    name: "Coffee",
    category: "beverage",
    colorClass: "food-coffee-text",
  },

  // OTHER
  { icon: Nut, id: "nut", name: "Nut", category: "other", colorClass: "food-nut-text" },
  { icon: Torus, id: "torus", name: "Torus", category: "other", colorClass: "food-torus-text" },
];

function getFoodIconsByCategories(categories: FoodCategory[]): FoodIcon[] {
  return FOOD_ICONS.filter((food) => categories.includes(food.category));
}

export function getRandomFoodIcon(categories?: FoodCategory[]): FoodIcon | null {
  const icons = categories ? getFoodIconsByCategories(categories) : FOOD_ICONS;

  if (icons.length === 0) {
    return null;
  }

  const randomIndex = Math.floor(Math.random() * icons.length);
  return icons[randomIndex];
}
